import React, { useState, useEffect } from 'react';
import { 
  FileText as PassportIcon, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  Download, 
  Save, 
  X, 
  Search, 
  AlertCircle,
  FileText,
  Shield,
  File,
  Eye,
  MapPin,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface PassportDocument {
  id: string;
  user_id: string;
  trip_id?: string;
  type: 'passport' | 'visa' | 'national_id' | 'drivers_license' | 'other';
  name: string;
  file_url?: string;
  document_number: string;
  issuing_country: string;
  issue_date: string;
  expiry_date: string;
  nationality?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Trip {
  id: string;
  title: string;
  destination?: string;
}

export function Passports() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<PassportDocument[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tripFilter, setTripFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<PassportDocument | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    trip_id: '',
    type: 'passport' as const,
    name: '',
    document_number: '',
    issuing_country: '',
    issue_date: '',
    expiry_date: '',
    nationality: '',
    notes: '',
    file: null as File | null
  });

  const typeOptions = [
    { value: 'passport', label: 'Passport', icon: <PassportIcon size={16} /> },
    { value: 'visa', label: 'Visa', icon: <FileText size={16} /> },
    { value: 'national_id', label: 'National ID', icon: <Shield size={16} /> },
    { value: 'drivers_license', label: 'Driver\'s License', icon: <File size={16} /> },
    { value: 'other', label: 'Other', icon: <FileText size={16} /> }
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const { data: tripsData, error: tripsError } = await supabase
          .from('trips')
          .select('id, title, destination')
          .eq('user_id', user.id)
          .order('start_date', { ascending: true });
          
        if (tripsError) throw tripsError;
        setTrips(tripsData || []);

        const { data: documentsData, error: documentsError } = await supabase
          .from('travel_documents')
          .select('*')
          .eq('user_id', user.id)
          .in('type', ['passport', 'visa', 'national_id', 'drivers_license', 'other'])
          .order('created_at', { ascending: false });
          
        if (documentsError) throw documentsError;
        setDocuments(documentsData || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const handleAddDocument = () => {
    setFormData({
      trip_id: '',
      type: 'passport',
      name: '',
      document_number: '',
      issuing_country: '',
      issue_date: '',
      expiry_date: '',
      nationality: '',
      notes: '',
      file: null
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  const handleEditDocument = (document: PassportDocument) => {
    setSelectedDocument(document);
    setFormData({
      trip_id: document.trip_id || '',
      type: document.type,
      name: document.name,
      document_number: document.document_number,
      issuing_country: document.issuing_country,
      issue_date: document.issue_date,
      expiry_date: document.expiry_date,
      nationality: document.nationality || '',
      notes: document.notes || '',
      file: null
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewDocument = (document: PassportDocument) => {
    setSelectedDocument(document);
    setShowViewModal(true);
  };

  const handleDeleteDocument = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        const { error } = await supabase
          .from('travel_documents')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        setDocuments(documents.filter(doc => doc.id !== id));
      } catch (err) {
        console.error('Error deleting document:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete document');
      }
    }
  };

  const handleFileUpload = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `travel-documents/${user!.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('travel-documents')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('travel-documents')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSaveDocument = async () => {
    if (!formData.name || !formData.document_number || !formData.issuing_country || !formData.issue_date || !formData.expiry_date) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setUploading(true);
      let fileUrl = selectedDocument?.file_url;

      if (formData.file) {
        fileUrl = await handleFileUpload(formData.file);
      }

      const documentData = {
        user_id: user!.id,
        trip_id: formData.trip_id || null,
        type: formData.type,
        name: formData.name,
        file_url: fileUrl,
        document_number: formData.document_number,
        issuing_country: formData.issuing_country,
        issue_date: formData.issue_date,
        expiry_date: formData.expiry_date,
        nationality: formData.nationality || null,
        notes: formData.notes || null
      };

      if (isEditing && selectedDocument) {
        const { data, error } = await supabase
          .from('travel_documents')
          .update(documentData)
          .eq('id', selectedDocument.id)
          .select()
          .single();
          
        if (error) throw error;
        setDocuments(documents.map(doc => doc.id === selectedDocument.id ? data : doc));
      } else {
        const { data, error } = await supabase
          .from('travel_documents')
          .insert(documentData)
          .select()
          .single();
          
        if (error) throw error;
        setDocuments([data, ...documents]);
      }
      
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error saving document:', err);
      setError(err instanceof Error ? err.message : 'Failed to save document');
    } finally {
      setUploading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    return typeOptions.find(t => t.value === type)?.icon || <FileText size={16} />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTripTitle = (tripId: string) => {
    return trips.find(t => t.id === tripId)?.title || 'Unknown Trip';
  };

  const isExpiringSoon = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 180 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.document_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.issuing_country.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || doc.type === typeFilter;
    const matchesTrip = tripFilter === 'all' || doc.trip_id === tripFilter;
    return matchesSearch && matchesType && matchesTrip;
  });

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Passports & Visas</h1>
            <p className="text-gray-600 mt-1">Manage your travel documents and identification</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Passports & Visas</h1>
          <p className="text-gray-600 mt-1">Manage your travel documents and identification</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddDocument}
          icon={<Plus size={20} />}
        >
          Add Document
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search documents..."
          className="flex-1"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {typeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={tripFilter}
          onChange={(e) => setTripFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Trips</option>
          {trips.map(trip => (
            <option key={trip.id} value={trip.id}>{trip.title}</option>
          ))}
        </select>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <Card className="text-center py-12">
          <PassportIcon className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents found</h3>
          <p className="text-gray-600 mb-4">Start by adding your first passport or visa</p>
          <Button
            variant="primary"
            onClick={handleAddDocument}
            icon={<Plus size={20} />}
          >
            Add Your First Document
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((document) => (
            <Card key={document.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{document.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-blue-600 mb-2">
                      {getTypeIcon(document.type)}
                      <span className="capitalize">{document.type}</span>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isExpired(document.expiry_date) 
                      ? 'bg-red-100 text-red-800' 
                      : isExpiringSoon(document.expiry_date)
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {isExpired(document.expiry_date) ? 'Expired' : 
                     isExpiringSoon(document.expiry_date) ? 'Expiring Soon' : 'Valid'}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Number:</span> {document.document_number}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Country:</span> {document.issuing_country}
                  </div>
                  {document.nationality && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Nationality:</span> {document.nationality}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={14} />
                    <span>Expires: {formatDate(document.expiry_date)}</span>
                  </div>
                </div>

                {document.notes && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{document.notes}</p>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Created {formatDate(document.created_at)}
                  </div>
                  <div className="flex items-center gap-2">
                    {document.file_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(document.file_url, '_blank')}
                        icon={<Download size={14} />}
                      >
                        Download
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDocument(document)}
                      icon={<Eye size={14} />}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditDocument(document)}
                      icon={<Edit size={14} />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteDocument(document.id)}
                      icon={<Trash2 size={14} />}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Document' : 'Add New Document'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip (Optional)</label>
            <select
              value={formData.trip_id}
              onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.title}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Number *</label>
              <input
                type="text"
                value={formData.document_number}
                onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 123456789"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Document name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Country *</label>
              <input
                type="text"
                value={formData.issuing_country}
                onChange={(e) => setFormData({ ...formData, issuing_country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Australia"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
              <input
                type="text"
                value={formData.nationality}
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Australian"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
              <input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document File</label>
            <input
              type="file"
              onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
            <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, PNG, DOC, DOCX</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            icon={<X size={16} />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveDocument}
            icon={uploading ? undefined : <Save size={16} />}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : (isEditing ? 'Update Document' : 'Create Document')}
          </Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedDocument?.name || 'Document Details'}
      >
        {selectedDocument && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex items-center gap-1 text-blue-600">
                  {getTypeIcon(selectedDocument.type)}
                  <span className="capitalize">{selectedDocument.type}</span>
                </div>
              </div>
              {selectedDocument.trip_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
                  <p className="text-gray-900">{getTripTitle(selectedDocument.trip_id)}</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Number</label>
              <p className="text-gray-900">{selectedDocument.document_number}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Country</label>
                <p className="text-gray-900">{selectedDocument.issuing_country}</p>
              </div>
              {selectedDocument.nationality && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                  <p className="text-gray-900">{selectedDocument.nationality}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                <p className="text-gray-900">{formatDate(selectedDocument.issue_date)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <div className="flex items-center gap-2">
                  <p className="text-gray-900">{formatDate(selectedDocument.expiry_date)}</p>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isExpired(selectedDocument.expiry_date) 
                      ? 'bg-red-100 text-red-800' 
                      : isExpiringSoon(selectedDocument.expiry_date)
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {isExpired(selectedDocument.expiry_date) ? 'Expired' : 
                     isExpiringSoon(selectedDocument.expiry_date) ? 'Expiring Soon' : 'Valid'}
                  </div>
                </div>
              </div>
            </div>

            {selectedDocument.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <p className="text-gray-900">{selectedDocument.notes}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-gray-900">{formatDate(selectedDocument.created_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-gray-900">{formatDate(selectedDocument.updated_at)}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          {selectedDocument?.file_url && (
            <Button
              variant="outline"
              onClick={() => window.open(selectedDocument.file_url, '_blank')}
              icon={<Download size={16} />}
            >
              Download File
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
          {selectedDocument && (
            <Button
              variant="primary"
              onClick={() => {
                setShowViewModal(false);
                handleEditDocument(selectedDocument);
              }}
              icon={<Edit size={16} />}
            >
              Edit Document
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
}